import React, { useState, useEffect } from 'react';
import {
  Card,
  Stack,
  Switch,
  FormControlLabel,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Alert,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../../components/minimal/iconify/Iconify';
import { useLocales } from '../../../locales';
import { useDashboardContext } from '../../../context-state/DashboardContext';
import { DayScheduleForm } from '../../../components/event/DayScheduleForm';
import { DateOverridesForm } from '../../../components/event/DateOverridesForm';
import { ConfigPreview } from '../../../components/event/ConfigPreview';
import { EventBreadcrumbs } from '../../common/EventBreadcrumbs';

export const TimeSlotSettingsSection = ({ onSubmit, isLoading, currentEvent: propEvent }) => {
  const { translate } = useLocales();

  // Try to get event from context if not passed as prop
  let currentEvent = propEvent;
  if (!currentEvent) {
    try {
      const { eventContext } = useDashboardContext();
      const { getEvent } = eventContext;
      currentEvent = getEvent(window.location.pathname.split('/')[6]); // Extract eventId from URL
    } catch (e) {
      // If context is not available, use a default event structure
      console.log('Using mock context or prop event');
    }
  }

  const [expandedDay, setExpandedDay] = useState('monday');
  const [hasChanges, setHasChanges] = useState(false);

  // Default schedule template
  const defaultDaySchedule = {
    enabled: true,
    startTime: "0900",
    endTime: "1700",
    slotDuration: 60,
    slotCapacity: 50,
    breakTimes: []
  };

  // Initialize config with proper defaults
  const initializeConfig = () => {
    const baseConfig = {
      enabled: false,
      defaultSlotDuration: 60,
      defaultCapacity: 50,
      daySchedules: {
        sunday: { ...defaultDaySchedule },
        monday: { ...defaultDaySchedule },
        tuesday: { ...defaultDaySchedule },
        wednesday: { ...defaultDaySchedule },
        thursday: { ...defaultDaySchedule },
        friday: { ...defaultDaySchedule, endTime: "1400" },
        saturday: { ...defaultDaySchedule, enabled: false, startTime: "1000", endTime: "1400" }
      },
      dateOverrides: [],
      bookingWindow: {
        advanceBookingDays: 30,
        cutoffMinutesBeforeSlot: 60
      }
    };

    // If we have an existing config, merge it with defaults
    if (currentEvent?.timeSlotConfig) {
      const existing = currentEvent.timeSlotConfig;
      return {
        ...baseConfig,
        ...existing,
        daySchedules: {
          ...baseConfig.daySchedules,
          ...(existing.daySchedules || {})
        },
        bookingWindow: {
          ...baseConfig.bookingWindow,
          ...(existing.bookingWindow || {})
        }
      };
    }

    return baseConfig;
  };

  const [config, setConfig] = useState(initializeConfig());

  // Track changes
  useEffect(() => {
    setHasChanges(JSON.stringify(config) !== JSON.stringify(currentEvent?.timeSlotConfig));
  }, [config, currentEvent]);

  const handleSave = () => {
    onSubmit(config);
    setHasChanges(false);
  };

  const handleDayScheduleChange = (dayType, schedule) => {
    setConfig({
      ...config,
      daySchedules: {
        ...config.daySchedules,
        [dayType]: schedule
      }
    });
  };

  const handleResetToDefault = () => {
    // Create a fresh default config without merging existing settings
    const baseConfig = {
      enabled: config.enabled, // Keep current enabled state
      defaultSlotDuration: 60,
      defaultCapacity: 50,
      daySchedules: {
        sunday: { ...defaultDaySchedule },
        monday: { ...defaultDaySchedule },
        tuesday: { ...defaultDaySchedule },
        wednesday: { ...defaultDaySchedule },
        thursday: { ...defaultDaySchedule },
        friday: { ...defaultDaySchedule, endTime: "1400" },
        saturday: { ...defaultDaySchedule, enabled: false, startTime: "1000", endTime: "1400" }
      },
      dateOverrides: [],
      bookingWindow: {
        advanceBookingDays: 30,
        cutoffMinutesBeforeSlot: 60
      }
    };
    setConfig(baseConfig);
    setHasChanges(true);
  };

  return (
    <>
      {/* Event Header with title, links and scanner */}
      {currentEvent && (
        <EventBreadcrumbs
          event={currentEvent}
          heading={translate('event_settings.time_slots')}
          sx={{ mb: 3 }}
        />
      )}

      <Grid container spacing={3}>
        {/* Left Panel - Configuration */}
        <Grid item xs={12} lg={7}>
        <Stack spacing={3}>
          {/* Enable/Disable Time Slots */}
          <Card sx={{ p: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.enabled}
                  disabled={isLoading}
                  onChange={(e) => {
                    const newEnabled = e.target.checked;
                    const newConfig = { ...config, enabled: newEnabled };
                    setConfig(newConfig);
                    // Immediately save when toggling enable/disable
                    onSubmit(newConfig);
                  }}
                />
              }
              label={
                <Stack>
                  <Typography variant="subtitle1">
                    {translate('event_settings.enable_time_slots')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {translate('event_settings.time_slots_info')}
                  </Typography>
                </Stack>
              }
            />
          </Card>

          {config.enabled && (
            <>
              {/* Alert Message */}
              <Alert severity="info">
                {translate('event_settings.time_slots_help')}
              </Alert>

              {/* Days Configuration - Individual Days as Rows */}
              <Stack spacing={2}>
                <Typography variant="h6">
                  {translate('event_settings.weekly_schedule')}
                </Typography>

                {[
                  { key: 'sunday', label: translate('days.sunday') },
                  { key: 'monday', label: translate('days.monday') },
                  { key: 'tuesday', label: translate('days.tuesday') },
                  { key: 'wednesday', label: translate('days.wednesday') },
                  { key: 'thursday', label: translate('days.thursday') },
                  { key: 'friday', label: translate('days.friday') },
                  { key: 'saturday', label: translate('days.saturday') }
                ].map(({ key, label }) => (
                  <Accordion
                    key={key}
                    expanded={expandedDay === key}
                    onChange={() => setExpandedDay(expandedDay === key ? null : key)}
                    sx={{
                      '&:before': { display: 'none' },
                      boxShadow: 1
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}
                      sx={{
                        bgcolor: config.daySchedules[key]?.enabled ? 'primary.lighter' : 'grey.100',
                        '&:hover': { bgcolor: config.daySchedules[key]?.enabled ? 'primary.light' : 'grey.200' }
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                        <Typography variant="h6" sx={{ minWidth: 120 }}>
                          {label}
                        </Typography>
                        <Chip
                          label={config.daySchedules[key]?.enabled ? translate('general.enabled') : translate('general.disabled')}
                          color={config.daySchedules[key]?.enabled ? 'success' : 'default'}
                          size="small"
                        />
                        {config.daySchedules[key]?.enabled && config.daySchedules[key]?.startTime && config.daySchedules[key]?.endTime && (
                          <Typography variant="body2" color="text.secondary">
                            {config.daySchedules[key].startTime.substring(0, 2)}:{config.daySchedules[key].startTime.substring(2, 4)} -
                            {' '}
                            {config.daySchedules[key].endTime.substring(0, 2)}:{config.daySchedules[key].endTime.substring(2, 4)}
                          </Typography>
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <DayScheduleForm
                        schedule={config.daySchedules[key] || defaultDaySchedule}
                        onChange={(schedule) => handleDayScheduleChange(key, schedule)}
                        title={label}
                      />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>

              {/* Special Dates Section */}
              <Card sx={{ mt: 2 }}>
                <Accordion
                  expanded={expandedDay === 'overrides'}
                  onChange={() => setExpandedDay(expandedDay === 'overrides' ? null : 'overrides')}
                  sx={{
                    '&:before': { display: 'none' },
                    boxShadow: 0
                  }}
                >
                  <AccordionSummary
                    expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}
                    sx={{ bgcolor: 'warning.lighter' }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography variant="h6">
                        {translate('event_settings.special_dates')}
                      </Typography>
                      <Chip
                        label={`${config.dateOverrides?.length || 0} ${translate('event_settings.dates_configured')}`}
                        color="warning"
                        size="small"
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <DateOverridesForm
                      overrides={config.dateOverrides}
                      onChange={(overrides) => setConfig({
                        ...config,
                        dateOverrides: overrides
                      })}
                    />
                  </AccordionDetails>
                </Accordion>
              </Card>

              {/* Booking Window Settings */}
              <Card sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {translate('event_settings.booking_window')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={translate('event_settings.advance_booking_days')}
                      value={config.bookingWindow.advanceBookingDays}
                      onChange={(e) => setConfig({
                        ...config,
                        bookingWindow: {
                          ...config.bookingWindow,
                          advanceBookingDays: parseInt(e.target.value) || 30
                        }
                      })}
                      InputProps={{ inputProps: { min: 1, max: 365 } }}
                      helperText={translate('event_settings.advance_booking_help')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={translate('event_settings.cutoff_minutes')}
                      value={config.bookingWindow.cutoffMinutesBeforeSlot}
                      onChange={(e) => setConfig({
                        ...config,
                        bookingWindow: {
                          ...config.bookingWindow,
                          cutoffMinutesBeforeSlot: parseInt(e.target.value) || 60
                        }
                      })}
                      InputProps={{ inputProps: { min: 0, max: 1440 } }}
                      helperText={translate('event_settings.cutoff_help')}
                    />
                  </Grid>
                </Grid>
              </Card>
            </>
          )}

          {/* Action Buttons */}
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleResetToDefault}
              startIcon={<Iconify icon="eva:refresh-outline" />}
            >
{translate('auth.reset_password').split(' ')[0]} {translate('charge_type.default')}
            </Button>

            <Stack direction="row" spacing={2}>
              {hasChanges && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setConfig(currentEvent?.timeSlotConfig || config);
                    setHasChanges(false);
                  }}
                >
                  {translate('general.cancel')}
                </Button>
              )}
              <LoadingButton
                loading={isLoading}
                variant="contained"
                onClick={handleSave}
                disabled={!hasChanges}
                startIcon={<Iconify icon="eva:save-outline" />}
              >
                {translate('general.save')}
              </LoadingButton>
            </Stack>
          </Stack>
        </Stack>
      </Grid>

      {/* Right Panel - Live Preview */}
      <Grid item xs={12} lg={5}>
        <Card sx={{ p: 3, bgcolor: 'grey.50', position: 'sticky', top: 20 }}>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Iconify icon="eva:eye-fill" width={24} />
              <Typography variant="h6">
                {translate('event_settings.live_preview')}
              </Typography>
            </Stack>

            <Divider />

            {config.enabled ? (
              <ConfigPreview
                config={config}
                activeDay={expandedDay || 'monday'}
                eventName={currentEvent?.name}
              />
            ) : (
              <Alert severity="info">
                {translate('event_settings.enable_preview_message')}
              </Alert>
            )}
          </Stack>
        </Card>
      </Grid>
    </Grid>
    </>
  );
};